// ─── File: ui/src/components/LogInsights/LogInsights.js ─────────────────────
// Date range picker (MUI X StaticDateRangePicker + dayjs) filters the whole
// dashboard — cards, charts, top errors, and table — client-side within the
// fixed 7-day data window. No extra API calls on date change.

import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column }    from "primereact/column";
import { Tag }       from "primereact/tag";
import { Toast }     from "primereact/toast";
import { Button }    from "primereact/button";
import { Dialog }    from "primereact/dialog";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import { useAuth as useOidcAuth } from "react-oidc-context";
import {
  getLogInsightsSummary,
  getLogInsightsErrors,
  createJiraBug,
} from "services/logInsightsService";
import { getConfigurationValues } from "services/userServices";
import classes from "./LogInsights.module.css";

import {
  Chart, BarController, BarElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale, Tooltip,
} from "chart.js";

Chart.register(
  BarController, BarElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale, Tooltip,
);

// ── Constants ─────────────────────────────────────────────────────────────────
const ERR_COLORS  = ["#E24B4A", "#EF9F27", "#378ADD", "#1D9E75", "#888780"];
const WINDOW_DAYS = 7;   // always fetch the full 7-day window

const FALLBACK_DEVELOPER_EMAILS = [
  "sarath.srimusuru@ttec.com",
  "anuranjan.prasad@ttec.com",
  "hitesh.kavar@ttec.com",
  "hema.jenu@ttec.com",
  "lavanya.madapathi@ttec.com",
  "saurabh.talekar@ttec.com",
  "mohammedrizwan.memon@ttec.com",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const truncate    = (str, max = 100) =>
  str && str.length > max ? str.substring(0, max) + "…" : (str ?? "");
const levelSeverity = (level) => (level === "ERR" ? "danger" : "warning");
const fmtDateTime   = (iso)   => new Date(iso).toLocaleString("en-IN", { hour12: false });

// ── Main component ────────────────────────────────────────────────────────────
export default function LogInsights() {
  const { user } = useOidcAuth();
  const userEmail = (
    user?.profile?.email ??
    user?.profile?.preferred_username ??
    user?.profile?.unique_name ??
    localStorage.getItem("EmailId") ??
    ""
  ).toLowerCase();

  const toast      = useRef(null);
  const barRef     = useRef(null);
  const donutRef   = useRef(null);
  const barChart   = useRef(null);
  const donutChart = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [accessState,    setAccessState]    = useState("checking");
  const [loading,        setLoading]        = useState(false);
  const [allSummary,     setAllSummary]     = useState(null);   // raw 7-day API response
  const [allErrors,      setAllErrors]      = useState([]);     // raw 7-day list

  // Date range: [start, end] as dayjs — default = full 7-day window
  const today         = dayjs().startOf("day");
  const windowStart   = today.subtract(WINDOW_DAYS - 1, "day");
  const [dateRange,    setDateRange]   = useState([windowStart, today]);

  const [jiraLoading,    setJiraLoading]    = useState({});
  const [jiraKeys,       setJiraKeys]       = useState({});
  const [confirmEntry,   setConfirmEntry]   = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pickerOpen,     setPickerOpen]     = useState(false);

  // ── Step 1: Access gate ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      try {
        const res = await getConfigurationValues("developerModeAccess");
        if (cancelled) return;
        const configVal = res?.data ?? "";
        const allowedEmails =
          configVal.trim().length > 0
            ? configVal.split(",").map((e) => e.trim().toLowerCase())
            : FALLBACK_DEVELOPER_EMAILS.map((e) => e.toLowerCase());
        setAccessState(allowedEmails.includes(userEmail) ? "granted" : "denied");
      } catch {
        if (cancelled) return;
        const allowed = FALLBACK_DEVELOPER_EMAILS.map((e) => e.toLowerCase());
        setAccessState(allowed.includes(userEmail) ? "granted" : "denied");
      }
    };
    checkAccess();
    return () => { cancelled = true; };
  }, [userEmail]);

  // ── Step 2: Load 7 days of raw data once ──────────────────────────────────
  useEffect(() => {
    if (accessState !== "granted") return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [sumRes, errRes] = await Promise.all([
          getLogInsightsSummary(WINDOW_DAYS),
          getLogInsightsErrors(WINDOW_DAYS),
        ]);
        if (cancelled) return;
        if (!sumRes?.status) return;
        setAllSummary(sumRes.data);
        setAllErrors(errRes?.data ?? []);
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessState]);

  // ── Step 3: Slice raw data by selected date range (client-side) ───────────
  const [rangeStart, rangeEnd] = dateRange;

  const filteredErrors = useMemo(() => {
    if (!rangeStart || !rangeEnd) return allErrors;
    const start = rangeStart.startOf("day").toDate();
    const end   = rangeEnd.endOf("day").toDate();
    return allErrors.filter((e) => {
      const t = new Date(e.timestamp);
      return t >= start && t <= end;
    });
  }, [allErrors, rangeStart, rangeEnd]);

  // Re-derive summary from filtered errors so all cards + charts stay in sync
  const summary = useMemo(() => {
    if (!allSummary) return null;
    const today = new Date(); today.setHours(23, 59, 59, 999);
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const errOnly = filteredErrors.filter((e) => e.level === "ERR" || e.Level === "ERR");
    const wrnOnly = filteredErrors.filter((e) => e.level === "WRN" || e.Level === "WRN");

    // Use Level (capitalised) as returned by the API
    const getLevel = (e) => e.Level ?? e.level ?? "";

    const errs = filteredErrors.filter((e) => getLevel(e) === "ERR");
    const wrns = filteredErrors.filter((e) => getLevel(e) === "WRN");

    // Daily trend for chart
    const byDay = {};
    filteredErrors.forEach((e) => {
      const d = new Date(e.timestamp);
      const key = d.toISOString().split("T")[0];
      if (!byDay[key]) byDay[key] = { errors: 0, warnings: 0, dayLabel: d.toLocaleDateString("en-US", { weekday: "short" }) };
      if (getLevel(e) === "ERR") byDay[key].errors++;
      if (getLevel(e) === "WRN") byDay[key].warnings++;
    });
    const dailyTrend = Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, v]) => ({ day, ...v }));

    // Top errors
    const msgGroups = {};
    errs.forEach((e) => {
      const key = (e.message ?? e.Message ?? "").substring(0, 80);
      if (!msgGroups[key]) msgGroups[key] = { messagePrefix: key, count: 0, lastSeen: e.timestamp };
      msgGroups[key].count++;
      if (new Date(e.timestamp) > new Date(msgGroups[key].lastSeen))
        msgGroups[key].lastSeen = e.timestamp;
    });
    const topErrors = Object.values(msgGroups)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalErrors:   errs.length,
      totalWarnings: wrns.length,
      todayErrors:   errs.filter((e) => {
        const t = new Date(e.timestamp);
        return t >= todayStart && t <= today;
      }).length,
      topErrors,
      dailyTrend,
    };
  }, [filteredErrors, allSummary]);

  // ── Build charts ───────────────────────────────────────────────────────────
  const buildCharts = useCallback(() => {
    if (!summary || !barRef.current || !donutRef.current) return;

    const isDark  = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const grid    = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
    const tickClr = isDark ? "rgba(255,255,255,0.5)"  : "rgba(0,0,0,0.45)";

    barChart.current?.destroy();
    donutChart.current?.destroy();

    const trend  = summary.dailyTrend ?? [];

    barChart.current = new Chart(barRef.current, {
      type: "bar",
      data: {
        labels: trend.map((d) => d.dayLabel),
        datasets: [
          { label: "Errors",   data: trend.map((d) => d.errors),
            backgroundColor: "#E24B4A", borderRadius: 5, borderSkipped: false },
          { label: "Warnings", data: trend.map((d) => d.warnings),
            backgroundColor: "#EF9F27", borderRadius: 5, borderSkipped: false },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false },
          tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${c.parsed.y}` } } },
        scales: {
          x: { grid: { color: grid }, ticks: { color: tickClr, font: { size: 11 } }, border: { display: false } },
          y: { grid: { color: grid }, ticks: { color: tickClr, font: { size: 11 }, stepSize: 1 }, border: { display: false }, beginAtZero: true },
        },
      },
    });

    const top5 = (summary.topErrors ?? []).slice(0, 5);
    donutChart.current = new Chart(donutRef.current, {
      type: "doughnut",
      data: {
        labels: top5.map((e) => truncate(e.messagePrefix, 30)),
        datasets: [{ data: top5.map((e) => e.count), backgroundColor: ERR_COLORS, borderWidth: 0, hoverOffset: 6 }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: "68%",
        plugins: { legend: { display: false },
          tooltip: { callbacks: { label: (c) => ` ${c.label}: ${c.parsed}` } } },
      },
    });
  }, [summary]);

  useEffect(() => {
    if (summary) buildCharts();
    return () => { barChart.current?.destroy(); donutChart.current?.destroy(); };
  }, [summary, buildCharts]);

  // ── Jira ───────────────────────────────────────────────────────────────────
  const handleCreateJira = async (entry, rowKey) => {
    setConfirmVisible(false);
    setJiraLoading((p) => ({ ...p, [rowKey]: true }));
    try {
      const res = await createJiraBug(entry.endpoint, entry.message, entry.stackTrace, userEmail);
      if (res?.status) {
        const key = res.message?.split(": ")[1] ?? "";
        setJiraKeys((p) => ({ ...p, [rowKey]: key }));
        toast.current.show({ severity: "success", summary: "Jira ticket created",
          detail: `${key} has been added to the ASTT board.`, life: 5000 });
      } else {
        toast.current.show({ severity: "error", summary: "Failed",
          detail: res?.message ?? "Could not create ticket. Please open manually.", life: 6000 });
      }
    } catch {
      toast.current.show({ severity: "error", summary: "Error",
        detail: "Something went wrong. Please try again.", life: 5000 });
    } finally {
      setJiraLoading((p) => ({ ...p, [rowKey]: false }));
    }
  };

  const openConfirm = (entry, rowKey) => { setConfirmEntry({ entry, rowKey }); setConfirmVisible(true); };

  // ── Column templates ───────────────────────────────────────────────────────
  const timestampBody = (row) => fmtDateTime(row.timestamp);
  const levelBody     = (row) => <Tag value={row.Level ?? row.level} severity={levelSeverity(row.Level ?? row.level)} />;
  const messageBody   = (row) => <span title={row.message ?? row.Message}>{truncate(row.message ?? row.Message, 100)}</span>;
  const endpointBody  = (row) => <span className={classes.endpoint}>{row.endpoint ?? row.Endpoint ?? "—"}</span>;

  const jiraBody = (row) => {
    const key = row.timestamp + (row.message ?? row.Message);
    if (jiraKeys[key])
      return <a href={`https://ttec-pmo.atlassian.net/browse/${jiraKeys[key]}`}
                target="_blank" rel="noreferrer" className={classes.jiraLink}>{jiraKeys[key]}</a>;
    if (jiraLoading[key])
      return <CircularProgress size={16} sx={{ color: "#3299FF" }} />;
    return (
      <button className={classes.jiraIconBtn} title="Create Jira Bug"
              onClick={() => openConfirm(row, key)}>
        <i className="pi pi-ticket" />
      </button>
    );
  };

  const rowClassName = (row) =>
    (row.Level ?? row.level) === "ERR" ? classes.rowError : classes.rowWarning;

  // ── Derived labels ─────────────────────────────────────────────────────────
  const todayErrors = summary?.todayErrors ?? 0;
  const maxErrCount = summary?.topErrors?.[0]?.count ?? 1;
  const top5Errors  = (summary?.topErrors ?? []).slice(0, 5);

  const isFullRange = rangeStart?.isSame(windowStart, "day") && rangeEnd?.isSame(today, "day");
  const isSingleDay = rangeStart && rangeEnd && rangeStart.isSame(rangeEnd, "day");

  const dateRangeLabel = !rangeStart || !rangeEnd
    ? "Select a date range"
    : isSingleDay
      ? rangeStart.format("DD MMM YYYY")
      : `${rangeStart.format("DD MMM")} – ${rangeEnd.format("DD MMM YYYY")}`;

  const cardLabel = isSingleDay ? rangeStart.format("DD MMM") : `${rangeStart?.format("DD MMM")} – ${rangeEnd?.format("DD MMM")}`;

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (accessState === "checking")
    return <div className={classes.centered}><CircularProgress sx={{ color: "#3299FF" }} /></div>;

  if (accessState === "denied")
    return (
      <div className={classes.accessDeniedBanner}>
        <i className="pi pi-lock" style={{ fontSize: 18, marginRight: 10 }} />
        <div>
          <div className={classes.accessDeniedTitle}>Access restricted</div>
          <div className={classes.accessDeniedSub}>Log Insights has limited access.</div>
        </div>
      </div>
    );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={classes.container}>
        <Toast ref={toast} />

        {/* ── Header ── */}
        <div className={classes.pageHeader}>
          <div>
            <h2 className={classes.pageTitle}>
              <i className="pi pi-chart-line" style={{ marginRight: 8, fontSize: 18, verticalAlign: -2 }} />
              Log Insights
            </h2>
            <div className={classes.subtitleRow}>
              <span className={classes.pageSubtitle}>Production</span>
              <span className={classes.subtitleSep}>·</span>
              {/* Date range pill — click to open picker */}
              <button
                className={`${classes.dateRangePill} ${pickerOpen ? classes.dateRangePillOpen : ""}`}
                onClick={() => setPickerOpen((p) => !p)}
              >
                <i className="pi pi-calendar" style={{ marginRight: 6, fontSize: 12 }} />
                {dateRangeLabel}
                <i className={`pi pi-chevron-${pickerOpen ? "up" : "down"}`}
                   style={{ marginLeft: 6, fontSize: 10 }} />
              </button>
              {/* Today quick-access button */}
              {!isSingleDay || !rangeStart?.isSame(today, "day") ? (
                <button
                  className={classes.todayBtn}
                  onClick={() => {
                    setDateRange([today, today]);
                    setPickerOpen(false);
                  }}
                >
                  <i className="pi pi-sun" style={{ marginRight: 4, fontSize: 11 }} />
                  Today
                </button>
              ) : null}
              {!isFullRange && (
                <button className={classes.resetBtn} onClick={() => {
                  setDateRange([windowStart, today]);
                  setPickerOpen(false);
                }}>
                  Reset
                </button>
              )}
            </div>
          </div>
          {todayErrors > 0 && (
            <span className={classes.todayBadge}>
              <i className="pi pi-exclamation-circle" style={{ marginRight: 4, fontSize: 12 }} />
              {todayErrors} error{todayErrors !== 1 ? "s" : ""} today
            </span>
          )}
        </div>

        {/* ── Date picker panel (two native date inputs) ── */}
        {pickerOpen && (
          <div className={classes.pickerPanel}>
            <div className={classes.pickerRow}>
              <div className={classes.pickerField}>
                <label className={classes.pickerLabel}>From</label>
                <input
                  type="date"
                  className={classes.pickerInput}
                  min={windowStart.format("YYYY-MM-DD")}
                  max={rangeEnd ? rangeEnd.format("YYYY-MM-DD") : today.format("YYYY-MM-DD")}
                  value={rangeStart ? rangeStart.format("YYYY-MM-DD") : ""}
                  onChange={(e) => {
                    const d = dayjs(e.target.value);
                    setDateRange([d, rangeEnd]);
                  }}
                />
              </div>
              <span className={classes.pickerSep}>→</span>
              <div className={classes.pickerField}>
                <label className={classes.pickerLabel}>To</label>
                <input
                  type="date"
                  className={classes.pickerInput}
                  min={rangeStart ? rangeStart.format("YYYY-MM-DD") : windowStart.format("YYYY-MM-DD")}
                  max={today.format("YYYY-MM-DD")}
                  value={rangeEnd ? rangeEnd.format("YYYY-MM-DD") : ""}
                  onChange={(e) => {
                    const d = dayjs(e.target.value);
                    setDateRange([rangeStart, d]);
                    setPickerOpen(false);  // close on end date select
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={28} sx={{ color: "#3299FF" }} />
            <span className={classes.loadingText}>Loading…</span>
          </div>
        ) : (
          <>
            {/* ── Metric cards ── */}
            <div className={classes.cardRow}>
              <div className={`${classes.card} ${classes.cardError}`}>
                <i className="pi pi-times-circle" style={{ fontSize: 20, color: "#E24B4A", marginBottom: 8, display: "block" }} />
                <div className={classes.cardLabel}>Errors ({cardLabel})</div>
                <div className={`${classes.cardValue} ${classes.valueError}`}>{summary?.totalErrors ?? 0}</div>
              </div>
              <div className={`${classes.card} ${classes.cardWarning}`}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: 20, color: "#EF9F27", marginBottom: 8, display: "block" }} />
                <div className={classes.cardLabel}>Warnings ({cardLabel})</div>
                <div className={`${classes.cardValue} ${classes.valueWarning}`}>{summary?.totalWarnings ?? 0}</div>
              </div>
              <div className={`${classes.card} ${classes.cardFlame}`}>
                <i className="pi pi-bolt" style={{ fontSize: 20, color: "#c04000", marginBottom: 8, display: "block" }} />
                <div className={classes.cardLabel}>Top error frequency</div>
                <div className={classes.cardValue}>{maxErrCount}×</div>
                <div className={classes.cardSub} title={summary?.topErrors?.[0]?.messagePrefix}>
                  {truncate(summary?.topErrors?.[0]?.messagePrefix ?? "—", 48)}
                </div>
              </div>
              <div className={`${classes.card} ${classes.cardInfo}`}>
                <i className="pi pi-ticket" style={{ fontSize: 20, color: "#378ADD", marginBottom: 8, display: "block" }} />
                <div className={classes.cardLabel}>Jira tickets (session)</div>
                <div className={`${classes.cardValue} ${classes.valueInfo}`}>{Object.keys(jiraKeys).length}</div>
              </div>
            </div>

            {/* ── Charts ── */}
            <div className={classes.chartsRow}>
              <div className={classes.panel}>
                <div className={classes.panelHeader}>
                  <span className={classes.panelTitle}>Error &amp; warning trend</span>
                  <div className={classes.legend}>
                    <span className={classes.legItem}><span className={classes.legDot} style={{ background: "#E24B4A" }} />Errors</span>
                    <span className={classes.legItem}><span className={classes.legDot} style={{ background: "#EF9F27" }} />Warnings</span>
                  </div>
                </div>
                <div className={classes.barWrap}>
                  <canvas ref={barRef} role="img" aria-label="Error and warning trend chart" />
                </div>
              </div>
              <div className={classes.panel}>
                <div className={classes.panelHeader}>
                  <span className={classes.panelTitle}>Error breakdown</span>
                </div>
                <div className={classes.donutWrap}>
                  <canvas ref={donutRef} role="img" aria-label="Error type breakdown donut chart" />
                </div>
                <div className={classes.donutLegend}>
                  {top5Errors.map((e, i) => (
                    <div key={i} className={classes.donutLegRow}>
                      <span className={classes.donutDot} style={{ background: ERR_COLORS[i] }} />
                      <span className={classes.donutLegLabel} title={e.messagePrefix}>{truncate(e.messagePrefix, 28)}</span>
                      <span className={classes.donutLegCount}>{e.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Top recurring errors ── */}
            {top5Errors.length > 0 && (
              <div className={classes.topErrorsPanel}>
                <div className={classes.panelTitle} style={{ marginBottom: 12 }}>Top recurring errors</div>
                {top5Errors.map((e, i) => (
                  <div key={i} className={classes.errBarRow}>
                    <span className={classes.errLevel}><Tag value="ERR" severity="danger" /></span>
                    <span className={classes.errMsg} title={e.messagePrefix}>{truncate(e.messagePrefix, 72)}</span>
                    <div className={classes.errTrack}>
                      <div className={classes.errFill}
                           style={{ width: `${Math.round((e.count / maxErrCount) * 100)}%`, background: ERR_COLORS[i] }} />
                    </div>
                    <span className={classes.errCount}>{e.count}</span>
                    <button className={classes.jiraIconBtn} title="Create Jira Bug"
                            onClick={() => openConfirm({ endpoint: null, message: e.messagePrefix, stackTrace: null }, `top_${i}`)}>
                      {jiraKeys[`top_${i}`]
                        ? <span className={classes.jiraKeySmall}>{jiraKeys[`top_${i}`]}</span>
                        : jiraLoading[`top_${i}`]
                          ? <CircularProgress size={14} sx={{ color: "#3299FF" }} />
                          : <i className="pi pi-ticket" />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ── DataTable ── */}
            <div className={classes.tableSection}>
              <div className={classes.panelTitle} style={{ marginBottom: 12 }}>
                Recent log entries
                <span className={classes.entryCount}>{filteredErrors.length} entries</span>
              </div>
              <DataTable value={filteredErrors} paginator rows={20} rowsPerPageOptions={[10, 20, 50, 100]}
                         size="small" stripedRows rowClassName={rowClassName} style={{ fontSize: 13 }}
                         emptyMessage="No errors or warnings found for the selected range.">
                <Column field="timestamp" header="Timestamp" style={{ width: 165 }} body={timestampBody} />
                <Column field="Level"     header="Level"     style={{ width: 72  }} body={levelBody}     />
                <Column field="endpoint"  header="Endpoint"  style={{ width: 210 }} body={endpointBody}  />
                <Column field="message"   header="Message"                          body={messageBody}   />
                <Column header="Jira"     style={{ width: 80, textAlign: "center" }} body={jiraBody}     />
              </DataTable>
            </div>
          </>
        )}

        {/* ── Confirm dialog ── */}
        <Dialog visible={confirmVisible} onHide={() => setConfirmVisible(false)}
                header="Create Jira Bug Ticket" style={{ width: 480 }}
                footer={
                  <div>
                    <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
                    <Button label="Create Ticket" icon="pi pi-ticket"
                            onClick={() => confirmEntry && handleCreateJira(confirmEntry.entry, confirmEntry.rowKey)} />
                  </div>
                }>
          {confirmEntry && (
            <div className={classes.confirmBody}>
              <p><strong>Endpoint:</strong> {confirmEntry.entry.endpoint ?? "N/A"}</p>
              <p><strong>Error:</strong> {truncate(confirmEntry.entry.message, 200)}</p>
              <p className={classes.confirmNote}>
                A Bug ticket will be created in the ASTT Jira board under your account ({userEmail}).
              </p>
            </div>
          )}
        </Dialog>
    </div>
  );
}
